import { contactsApi, apiWithContext } from './api';
import { Contact, ContactFilters, PaginatedResponse, ContactFormData } from '../types/contact';

export const getContacts = async (filters: ContactFilters = {}): Promise<Contact[]> => {
  try {
    const response = await contactsApi.getContacts(filters);
    return response.contacts || [];
  } catch (error) {
    console.error('Error fetching contacts:', error);
    throw error;
  }
};

export const getContactById = async (id: string): Promise<Contact> => {
  try {
    return await contactsApi.getContact(id);
  } catch (error) {
    console.error(`Error fetching contact with id ${id}:`, error);
    throw error;
  }
};

// Get all customers (contacts with type 'customer')
export const getCustomers = async (filters: Omit<ContactFilters, 'type'> = {}): Promise<Contact[]> => {
  try {
    const response = await contactsApi.getContacts({
      ...filters,
      type: 'customer',
      status: 'active' // Only get active customers by default
    });
    return response.contacts || [];
  } catch (error) {
    console.error('Error fetching customers:', error);
    throw error;
  }
};

// Get all suppliers (contacts with type 'supplier')
export const getSuppliers = async (filters: Omit<ContactFilters, 'type'> = {}): Promise<Contact[]> => {
  try {
    const response = await contactsApi.getContacts({
      ...filters,
      type: 'supplier',
      status: 'active' // Only get active suppliers by default
    });
    return response.contacts || [];
  } catch (error) {
    console.error('Error fetching suppliers:', error);
    throw error;
  }
};

// البحث عن العملاء بواسطة رقم الهاتف
export const searchCustomersByPhone = async (phone: string): Promise<Contact[]> => {
  try {
    if (!phone || phone.trim().length < 3) {
      console.log('رقم الهاتف فارغ أو أقل من 3 أرقام');
      return [];
    }
    
    console.log(`بدء البحث عن العملاء برقم الهاتف: "${phone.trim()}"`);
    
    // استخدام معامل البحث العام مع إعداد خاص بالعملاء النشطين
    const response = await contactsApi.getContacts({
      type: 'customer',
      status: 'active',
      search: phone.trim()
    });
    
    console.log(`تم استلام ${response.contacts?.length || 0} عميل من الخادم`);
    
    // تصفية أكثر مرونة للنتائج - البحث عن أي تطابق جزئي في رقم الهاتف
    const filteredContacts = response.contacts?.filter(contact => {
      // التأكد من وجود رقم هاتف للعميل
      if (!contact.phone) return false;
      
      // تنظيف أرقام الهواتف من الأحرف الخاصة للمقارنة
      const cleanedContactPhone = contact.phone.replace(/[\s-+()]/g, '');
      const cleanedSearchPhone = phone.trim().replace(/[\s-+()]/g, '');
      
      // البحث عن تطابق جزئي في رقم الهاتف
      return cleanedContactPhone.includes(cleanedSearchPhone);
    }) || [];
    
    console.log(`تم العثور على ${filteredContacts.length} عميل بعد التصفية، أسماؤهم:`);
    filteredContacts.forEach((contact, index) => {
      console.log(`${index + 1}. ${contact.name} - ${contact.phone}`);
    });
    
    return filteredContacts;
  } catch (error) {
    console.error('خطأ في البحث عن العملاء برقم الهاتف:', error);
    throw error;
  }
};

// إنشاء عميل جديد
export const createCustomer = async (customerData: ContactFormData): Promise<Contact> => {
  try {
    // التأكد من أن نوع العميل 'customer'
    const data: ContactFormData = {
      ...customerData,
      type: 'customer',
      status: 'active',
      category: customerData.category || 'regular'
    };
    
    return await contactsApi.createContact(data);
  } catch (error) {
    console.error('Error creating customer:', error);
    throw error;
  }
};

// إنشاء عميل جديد مع سياق الفاتورة
export const createCustomerWithContext = async (customerData: ContactFormData): Promise<Contact> => {
  try {
    // التأكد من أن نوع العميل 'customer'
    const data: ContactFormData = {
      ...customerData,
      type: 'customer',
      status: 'active',
      category: customerData.category || 'regular'
    };
    
    const response = await apiWithContext.post('/contacts', data, 'invoice');
    return response.data;
  } catch (error) {
    console.error('Error creating customer with context:', error);
    throw error;
  }
};

export default {
  getContacts,
  getContactById,
  getCustomers,
  getSuppliers,
  searchCustomersByPhone,
  createCustomer,
  createCustomerWithContext
};